'use client';

import { useState } from 'react';
import { Plus, Zap } from 'lucide-react';
import { SESSION_TEMPLATES, BALL_LEVEL_COLOURS, type SessionTemplate } from '@/shared/sessionTemplates';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDirectorSessions,
  useDirectorCoaches,
  useCreateSession,
} from '@/hooks/use-director-data';
import { SessionTable } from '@/components/director/sessions/session-table';
import { CreateSessionForm } from '@/components/director/sessions/create-session-form';

export default function DirectorSessionsPage() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [templateData, setTemplateData] = useState<{
    sessionType?: string;
    startTime?: string;
    endTime?: string;
  } | undefined>();

  const handleOpenFromTemplate = (template: SessionTemplate) => {
    const defaultStart = '09:00';
    const durationH = Math.floor(template.durationMinutes / 60);
    const durationM = template.durationMinutes % 60;
    const [sh, sm] = defaultStart.split(':').map(Number);
    const totalEnd = sh * 60 + sm + durationH * 60 + durationM;
    const endTime = `${String(Math.floor(totalEnd / 60) % 24).padStart(2, '0')}:${String(totalEnd % 60).padStart(2, '0')}`;

    setTemplateData({
      sessionType: template.sessionType,
      startTime: defaultStart,
      endTime,
    });
    setCreateModalOpen(true);
  };

  const { sessions, status, loadMore, isLoading } = useDirectorSessions();
  const { data: coachesData } = useDirectorCoaches();
  const createSession = useCreateSession();

  const handleCreate = async (data: {
    date: string;
    startTime: string;
    endTime: string;
    sessionType?: string;
    courtNumber?: number;
    coachUserId?: string;
    repeat: string;
    repeatUntil: string;
  }) => {
    // Compute all dates to create based on repeat setting
    const dates: string[] = [data.date];
    if (data.repeat !== 'none' && data.repeatUntil && data.repeatUntil > data.date) {
      const intervalDays = data.repeat === 'weekly' ? 7 : data.repeat === 'fortnightly' ? 14 : null;
      const start = new Date(data.date + 'T00:00:00');
      const end = new Date(data.repeatUntil + 'T00:00:00');

      if (intervalDays) {
        const cursor = new Date(start);
        cursor.setDate(cursor.getDate() + intervalDays);
        while (cursor <= end) {
          dates.push(cursor.toISOString().split('T')[0]);
          cursor.setDate(cursor.getDate() + intervalDays);
        }
      } else {
        const cursor = new Date(start);
        cursor.setMonth(cursor.getMonth() + 1);
        while (cursor <= end) {
          dates.push(cursor.toISOString().split('T')[0]);
          cursor.setMonth(cursor.getMonth() + 1);
        }
      }
    }

    const seriesId = dates.length > 1 ? crypto.randomUUID() : undefined;

    const base = {
      startTime: data.startTime,
      endTime: data.endTime,
      sessionType: data.sessionType || undefined,
      courtNumber: data.courtNumber,
      coachUserId: data.coachUserId,
      seriesId,
    };

    await Promise.all(dates.map((date) => createSession.mutateAsync({ ...base, date })));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Sessions</h1>
          <p className="text-muted-foreground">Schedule and manage training sessions</p>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          className="self-start sm:self-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Session
        </Button>
      </div>

      {/* Quick Start Templates */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Zap className="w-4 h-4" />
          Quick Start
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {SESSION_TEMPLATES.map((t) => {
            const colours = BALL_LEVEL_COLOURS[t.ballLevel];
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleOpenFromTemplate(t)}
                className={`flex-shrink-0 w-40 rounded-xl border-2 border-border p-3 text-left hover:border-primary hover:bg-primary/5 transition-colors ${colours.bg}`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${colours.dot}`} />
                  <span className={`text-xs font-semibold uppercase tracking-wide ${colours.text}`}>
                    {t.ballLevel === 'mixed' ? 'Mixed' : `${t.ballLevel.charAt(0).toUpperCase() + t.ballLevel.slice(1)} Ball`}
                  </span>
                </div>
                <p className="text-sm font-semibold text-foreground leading-tight">{t.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.ageRange} · {t.durationMinutes} min</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Session List */}
      <SessionTable
        sessions={sessions}
        total={sessions.length}
        isLoading={isLoading}
        onCreateSession={() => setCreateModalOpen(true)}
      />
      {status === 'CanLoadMore' && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" onClick={() => loadMore(50)}>
            Load More
          </Button>
        </div>
      )}
      {status === 'LoadingMore' && (
        <div className="flex justify-center pt-2">
          <Skeleton className="h-10 w-28" />
        </div>
      )}

      {/* Create Session Modal */}
      <CreateSessionForm
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        coaches={coachesData?.coaches ?? []}
        onCreate={handleCreate}
        isPending={createSession.isPending}
        initialData={templateData}
      />
    </div>
  );
}
